import type { StorefrontSettings } from "@workspace/api-client-react";
import type { StoreProduct } from "@/lib/store";
import { getProductPieceCount } from "@/lib/estimated-pricing";

function formatNumber(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

export function formatDeliveryNoteText(
  template: string,
  ratePerKgBDT: number | null,
  includedWeightKg: number | null,
): string | null {
  if (!template.trim() || typeof ratePerKgBDT !== "number" || !Number.isFinite(ratePerKgBDT)) return null;
  const usesWeight = /\{included_weight_(?:kg|g)\}/.test(template);
  if (usesWeight && (typeof includedWeightKg !== "number" || !Number.isFinite(includedWeightKg))) return null;

  return template
    .replace(/\{rate_per_gram\}/g, formatNumber(ratePerKgBDT / 1000))
    .replace(/\{rate_per_kg\}/g, formatNumber(ratePerKgBDT))
    .replace(/\{included_weight_kg\}/g, includedWeightKg === null ? "" : formatNumber(includedWeightKg))
    .replace(/\{included_weight_g\}/g, includedWeightKg === null ? "" : formatNumber(includedWeightKg * 1000));
}

export function getDeliveryNoteText(
  product: Pick<StoreProduct, "pieceCount" | "body_html" | "description">,
  settings: StorefrontSettings,
): string | null {
  if (!settings.deliveryNoteEnabled) return null;

  const pieceCount = getProductPieceCount(product);
  if (pieceCount === null && settings.deliveryWithoutPieceCountMode === "not-included") {
    return formatDeliveryNoteText(settings.deliveryNotIncludedText, settings.shippingRatePerKgBDT, null);
  }
  const includedWeightKg = pieceCount === null
    ? settings.defaultDeliveryWeightKg
    : settings.pieceCountWeightsKg[String(pieceCount)] ?? null;
  const template = pieceCount === null ? settings.deliveryTextB : settings.deliveryTextA;

  return formatDeliveryNoteText(template, settings.shippingRatePerKgBDT, includedWeightKg);
}

export function getDeliveryStatusText(
  product: Pick<StoreProduct, "pieceCount" | "body_html" | "description">,
  settings: StorefrontSettings,
  withAsterisk = false,
): string | null {
  if (!settings.deliveryNoteEnabled) return null;
  const pieceCount = getProductPieceCount(product);
  if (pieceCount === null && settings.deliveryWithoutPieceCountMode === "not-included") {
    return "Delivery not included";
  }
  return getDeliveryNoteText(product, settings)
    ? `Delivery included${withAsterisk ? "*" : ""}`
    : null;
}

export function DeliveryNote({
  product,
  settings,
  className = "",
}: {
  product: Pick<StoreProduct, "pieceCount" | "body_html" | "description">;
  settings: StorefrontSettings;
  className?: string;
}) {
  const text = getDeliveryNoteText(product, settings);
  if (!text) return null;
  return <p className={`storefront-supporting-copy ${className}`} data-testid="text-delivery-note">{text}</p>;
}

export function DeliveryNotes({
  products,
  settings,
  className = "",
}: {
  products: readonly Pick<StoreProduct, "pieceCount" | "body_html" | "description">[];
  settings: StorefrontSettings;
  className?: string;
}) {
  if (products.length === 0 || !settings.deliveryBagMessage.trim()) return null;
  return <div className={className} data-testid="list-delivery-notes">
    <p className="storefront-supporting-copy" data-testid="text-delivery-note-0">{settings.deliveryBagMessage}</p>
  </div>;
}